import * as Sentry from "@sentry/react";

/** Vite only exposes env vars prefixed with VITE_. Trim + strip accidental quotes from .env files. */
function normalizeSentryDsn(raw: string | undefined): string | undefined {
  if (raw == null) return undefined;
  let s = String(raw).trim();
  if (
    (s.startsWith('"') && s.endsWith('"')) ||
    (s.startsWith("'") && s.endsWith("'"))
  ) {
    s = s.slice(1, -1).trim();
  }
  return s || undefined;
}

const dsn = normalizeSentryDsn(
  import.meta.env.VITE_SENTRY_DSN as string | undefined
);

const sentryDebug = import.meta.env.VITE_SENTRY_DEBUG === "true";

/** Set VITE_SENTRY_LOGS=false to hide these informational lines (SDK debug is separate: VITE_SENTRY_DEBUG). */
const sentryInfoLogs = import.meta.env.VITE_SENTRY_LOGS !== "false";

function logSentry(...args: unknown[]) {
  if (!sentryInfoLogs) return;
  console.info("[sentry]", ...args);
}

function warnIfDsnLooksInvalid(value: string) {
  const looksOk = value.startsWith("https://") && value.includes("@");
  if (!looksOk) {
    console.warn(
      "[sentry] VITE_SENTRY_DSN should be a full https DSN from Sentry → Settings → Client Keys (includes @)."
    );
  }
}

function commonSentryOptions(): Omit<Sentry.BrowserOptions, "dsn"> {
  return {
    environment: import.meta.env.MODE,
    sendDefaultPii: true,
    enabled: true,
    debug: sentryDebug,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend(event) {
      if (sentryDebug) {
        console.info("[sentry] beforeSend", {
          event_id: event.event_id,
          level: event.level,
          exception: event.exception,
        });
      }
      return event;
    },
  };
}

/**
 * Whether we currently have a live, consented Sentry client. Needed because
 * Sentry.close() keeps the (disabled) client bound, so getClient() alone
 * can't tell "running" from "shut down after consent withdrawal".
 */
let sentryActive = false;

export function initErrorTracking() {
  if (!dsn) {
    logSentry(
      "disabled — VITE_SENTRY_DSN is not set (browser errors will not be sent)"
    );
    if (sentryDebug) console.info("[sentry] debug: empty DSN, init skipped");
    return;
  }
  if (sentryActive) {
    // Already running (e.g. consent re-saved with errorTracking still on).
    return;
  }

  warnIfDsnLooksInvalid(dsn);

  Sentry.init({
    dsn,
    ...commonSentryOptions(),
  });
  sentryActive = true;

  logSentry("initialized", {
    environment: import.meta.env.MODE,
    ingestHost: getSentryDsnHostHint() ?? "(parse failed)",
    client: Boolean(Sentry.getClient()),
    sdkDebug: sentryDebug,
  });
  if (sentryDebug) {
    console.info("[sentry] debug: full init complete");
  }
}

/**
 * Stop reporting after the user withdraws the error-tracking consent
 * (GDPR Art. 7(3)). Flushes pending events, then disables the client.
 * A later initErrorTracking() (consent re-granted) binds a fresh client.
 */
export function shutdownErrorTracking() {
  if (!sentryActive) return;
  sentryActive = false;
  logSentry("shutting down — error-tracking consent not granted");
  void Sentry.close(2000);
}

/** True when VITE_SENTRY_DSN is set (embedded at build time). */
export function isSentryReportingActive() {
  return !!dsn;
}

/** Whether this bundle was built with VITE_SENTRY_DSN (Vite inlines it at build time). */
export function hasEmbeddedSentryDsn() {
  return !!dsn;
}

/**
 * If Sentry isn’t initialized yet but DSN is embedded, init once (e.g. race with lazy entry).
 */
export function ensureSentryClientForSmokeTest() {
  if (!dsn) {
    logSentry("ensureSentryClientForSmokeTest: no DSN, skipping");
    return;
  }
  if (Sentry.getClient()) {
    logSentry("ensureSentryClientForSmokeTest: client already exists");
    return;
  }
  logSentry("ensureSentryClientForSmokeTest: calling Sentry.init (late init)");
  warnIfDsnLooksInvalid(dsn);
  Sentry.init({
    dsn,
    ...commonSentryOptions(),
  });
  sentryActive = true;
  logSentry("ensureSentryClientForSmokeTest: init done", {
    ingestHost: getSentryDsnHostHint() ?? "(parse failed)",
  });
}

export function setErrorTrackingUser(user: { id: string } | null) {
  if (!dsn) return;
  Sentry.setUser(user);
  if (sentryDebug)
    console.info("[sentry] setUser", user ? { id: user.id } : null);
}

export function setErrorTrackingTags(
  tags: Record<string, string | number | boolean | null | undefined>
) {
  if (!dsn) return;
  for (const [k, v] of Object.entries(tags)) {
    if (v === undefined || v === null) continue;
    Sentry.setTag(k, String(v));
  }
  if (sentryDebug) console.info("[sentry] setTags", tags);
}

/** Safe ingest host for UI hints (no secret leaked). */
export function getSentryDsnHostHint(): string | null {
  if (!dsn) return null;
  const m = dsn.match(/@([^/]+)\//);
  return m?.[1] ?? null;
}
